import { Button } from 'components/ui'
import useIsMacOS from 'hooks/use-is-mac-os'
import useIsTouchDevice from 'hooks/use-is-touch-device'
import { FC, ReactElement, useEffect } from 'react'

interface Props {
  title: string
  message: ReactElement | string
  success?: boolean
  danger?: boolean
  confirmText?: string
  dismissText?: string
  cyDataValue?: string
  showBypassTip?: boolean
  onConfirm?: () => any
  onDismiss?: () => any
}

export const Confirmation: FC<Props> = ({
  title,
  message,
  success = false,
  danger = false,
  confirmText = 'Confirm',
  dismissText = 'Cancel',
  showBypassTip = false,
  onConfirm = null,
  onDismiss = null,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Confirm on Enter or Space
      if (e.key === 'Enter' || e.key === ' ') {
        // Prevent e.g. forms from submitting twice
        e.preventDefault()
        onConfirm()
      }
      // Dismiss on Escape
      else if (e.key === 'Escape') {
        e.preventDefault()
        onDismiss()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onConfirm, onDismiss])

  const isTouchDevice = useIsTouchDevice()
  const isMac = useIsMacOS()

  return (
    <div
      role="alertdialog"
      aria-labelledby="dialogTitle"
      className="mx-auto rounded-lg bg-white p-4 font-body dark:bg-gray-500">
      <div className="w-full text-center"></div>
      <div className="text-left">
        <div
          id="dialogTitle"
          className="border-b border-gray-100 pb-2 text-xl font-bold text-gray">
          {title}
        </div>
        <div
          id="dialogDesc"
          className="py-4 text-base font-semibold leading-5 text-gray">
          {message}
        </div>
        {showBypassTip && !isTouchDevice && (
          <div className="text-xs font-semibold text-gray-300">
            Hold <kbd>{isMac ? 'Option' : 'Alt'}</kbd> to skip this
            confirmation.
          </div>
        )}
      </div>
      <div className="flex w-full flex-col-reverse gap-2 pt-2">
        {!success && (
          <Button
            variant="large"
            className="w-full flex-1 px-0 text-sm"
            onClick={onDismiss}>
            {dismissText}
          </Button>
        )}
        <Button
          variant="large"
          danger={danger}
          primary={success}
          onClick={onConfirm}
          className="w-full flex-1 text-sm">
          {confirmText}
        </Button>
      </div>
    </div>
  )
}
